/// <amd-module name="Build/Run/Components/Canvas/Edge" />
import * as React from "react";

import { VssComponent, IVssComponentProperties } from "VSS/Platform/Layout";
import { IPoint, Constants } from "Build/Run/Components/Canvas/Types";
import { PathBuilder } from "Build/Run/Components/Canvas/PathHelpers";

import { css } from "VSSUI/Util";

export interface IEdgeProps extends IVssComponentProperties {
    
    from: IPoint;

    to: IPoint;

    /**
     * Indicates the vertical displacement of from point. This information
     * is used to space edges. 
     */
    fromDepth?: number;

    /**
     * Indicates the vertical displacement of quadraticBezier
     * in edge
     */
    quadraticBezierVerticalDisplacement?: number;

    intermediate1?: IPoint;

    intermediate2?: IPoint;
}

// Draws edge that is meant for a particular layout of graph that is used in CI/CD. 
export class Edge extends VssComponent<IEdgeProps, {}> {

    public render(): JSX.Element {

        let { from, to } = this.props;
        let pathAttribute = "";
        let intermediate1 = this.props.intermediate1;
        let intermediate2 = this.props.intermediate2;

        if (from.x > to.x) {
            console.error("Edge can only be drawn from left to right.");
        }

        if (intermediate1 !== undefined && intermediate2 !== undefined) {
            let pathBuilder = new PathBuilder().start(from);
            this._addEdgeSegment(pathBuilder, from, intermediate1);
            this._addEdgeSegment(pathBuilder, intermediate1, intermediate2);
            this._addEdgeSegment(pathBuilder, intermediate2, to);
            pathAttribute = pathBuilder.toString();
        }
        else {
            let pathBuilder = new PathBuilder().start(from);
            this._addEdgeSegment(pathBuilder, from, to);
            pathAttribute = pathBuilder.toString(); 
        }

        return <path className={css("dtc-canvas-path", this.props.cssClass)} d={pathAttribute} />;
    }

    private _addEdgeSegment(pathBuilder: PathBuilder, from: IPoint, to: IPoint) {
        if (from.y === to.y || from.x === to.x) {
            pathBuilder.line(to);
        }
        else if (from.y > to.y) {
            this._addUpwardCurve(pathBuilder, from, to);
        }
        else {
            this._addDownwardCurve(pathBuilder, from, to);
        }
    }

    private _addUpwardCurve(pathBuilder: PathBuilder, from: IPoint, to: IPoint) {
        let fromDepth = 0;
        let horizontalDistance = to.x - from.x;
        let intermediatePointX = from.x + Math.min(horizontalDistance / 2, horizontalDistance);
        pathBuilder
            .quadraticBezier(
            {
                x: intermediatePointX,
                y: from.y - this._getQuadraticBezierVerticalDisplacement()
            },
            {
                x: intermediatePointX,
                y: from.y
            })
            .line({ x: intermediatePointX, y: to.y + this._getQuadraticBezierVerticalDisplacement() }) // Vertical line at the center
            .quadraticBezier(
            {
                x: to.x,
                y: to.y
            },
            {
                x: intermediatePointX,
                y: to.y
            });
    }

    private _addDownwardCurve(pathBuilder: PathBuilder, from: IPoint, to: IPoint) {
        let fromDepth = 0;
        let horizontalDistance = to.x - from.x;
        let intermediatePointX = from.x + Math.max(horizontalDistance / 2, 0);
        pathBuilder
            .quadraticBezier(
            {
                x: intermediatePointX,
                y: from.y + this._getQuadraticBezierVerticalDisplacement()
            },
            {
                x: intermediatePointX,
                y: from.y
            })
            .line({ x: intermediatePointX, y: to.y - this._getQuadraticBezierVerticalDisplacement() }) // Vertical line at the center
            .quadraticBezier(
            {
                x: to.x,
                y: to.y
            },
            {
                x: intermediatePointX,
                y: to.y
            });
    }

    private _getQuadraticBezierVerticalDisplacement(): number {
        if (this.props.quadraticBezierVerticalDisplacement) {
            return this.props.quadraticBezierVerticalDisplacement;
        }
        return Constants.verticalDisplacement;
    }
}
